/* eslint-disable */
import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { motion } from "framer-motion";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Button,
  Chip,
  CircularProgress,
  Typography,
  IconButton,
  InputAdornment,
  Stack,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Divider,
  Grid,
  Tooltip,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
} from "@mui/material";
import {
  Add as AddIcon,
  Search as SearchIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Event as EventIcon,
  AttachMoney as MoneyIcon,
  Business as BusinessIcon,
  Close as CloseIcon,
  Visibility as ViewIcon,
  Receipt as ReceiptIcon,
  Edit as EditIcon,
  Download as DownloadIcon,
  Print as PrintIcon,
  Pending as PendingIcon,
  CheckCircle as ApprovedIcon,
  Payment as PaidIcon,
  Error as FailedIcon,
  CalendarToday as CalendarIcon,
  ReceiptLong as PayrollIcon,
  Percent as PercentIcon,
} from "@mui/icons-material";
import {
  fetchMonthlyPayrollReport,
  updatePayrollTransactionStatus,
} from "store/features/company/payrollManagement/processPayroll/processPayrollActions";
import {
  selectPayrollLoading,
  selectPayrollTransactions,
  selectPayrollSummary,
  selectProcessInfo,
} from "store/features/company/payrollManagement/processPayroll/processPayrollSelectors";
import AddProcessPayrollModal from "./AddProcessPayrollModal";
import EditTransactionStatusModal from "./EditTransactionStatusModal";
import DownloadPayslipModal from "./DownloadPayslipModal";
import processPayrollSvg from "assets/folders/payroll.svg";
import viewDetailsSvg from "assets/modals/details.svg";

const ProcessPayroll = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const loading = useSelector(selectPayrollLoading);
  const transactions = useSelector(selectPayrollTransactions);
  const summary = useSelector(selectPayrollSummary);
  const processInfo = useSelector(selectProcessInfo);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [viewingTransaction, setViewingTransaction] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(new Date().getFullYear());
  const [statusFilter, setStatusFilter] = useState("all");

  const [showDownloadModal, setShowDownloadModal] = useState(false);

  useEffect(() => {
    dispatch(fetchMonthlyPayrollReport({ month, year }));
  }, [dispatch, month, year]);

  const filteredTransactions = transactions.filter((transaction) => {
    const matchesSearch =
      searchTerm === "" ||
      transaction.employee_name
        ?.toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      transaction.employee_code
        ?.toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      transaction.department_name
        ?.toLowerCase()
        .includes(searchTerm.toLowerCase());

    const matchesStatus =
      statusFilter === "all" || transaction.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatCurrency = (amount) => {
    if (!amount) return "₹0";
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(parseFloat(amount));
  };

  const getStatusChip = (status) => {
    switch (status) {
      case "approved":
        return (
          <Chip
            icon={<ApprovedIcon sx={{ fontSize: 14 }} />}
            label="Approved"
            size="small"
            sx={{
              backgroundColor: "#dbeafe",
              color: "#1e40af",
              fontWeight: 500,
              fontSize: "0.75rem",
              height: "24px",
            }}
          />
        );
      case "paid":
        return (
          <Chip
            icon={<PaidIcon sx={{ fontSize: 14 }} />}
            label="Paid"
            size="small"
            sx={{
              backgroundColor: "#d1fae5",
              color: "#065f46",
              fontWeight: 500,
              fontSize: "0.75rem",
              height: "24px",
            }}
          />
        );
      case "failed":
        return (
          <Chip
            icon={<FailedIcon sx={{ fontSize: 14 }} />}
            label="Failed"
            size="small"
            sx={{
              backgroundColor: "#fee2e2",
              color: "#991b1b",
              fontWeight: 500,
              fontSize: "0.75rem",
              height: "24px",
            }}
          />
        );
      case "processed":
        return (
          <Chip
            icon={<PendingIcon sx={{ fontSize: 14 }} />}
            label="Processed"
            size="small"
            sx={{
              backgroundColor: "#e0e7ff",
              color: "#3730a3",
              fontWeight: 500,
              fontSize: "0.75rem",
              height: "24px",
            }}
          />
        );
      case "pending":
        return (
          <Chip
            icon={<PendingIcon sx={{ fontSize: 14 }} />}
            label="Pending"
            size="small"
            sx={{
              backgroundColor: "#fef3c7",
              color: "#92400e",
              fontWeight: 500,
              fontSize: "0.75rem",
              height: "24px",
            }}
          />
        );
      default:
        return (
          <Chip
            label={status || "Unknown"}
            size="small"
            sx={{
              backgroundColor: "#e5e7eb",
              color: "#374151",
              fontWeight: 500,
              fontSize: "0.75rem",
              height: "24px",
            }}
          />
        );
    }
  };

  const getProcessStatusChip = (status) => {
    switch (status) {
      case "completed":
        return (
          <Chip
            icon={<CheckCircleIcon sx={{ fontSize: 14 }} />}
            label="Completed"
            size="small"
            sx={{
              backgroundColor: "#d1fae5",
              color: "#065f46",
              fontWeight: 500,
              fontSize: "0.75rem",
              height: "24px",
            }}
          />
        );
      case "processing":
        return (
          <Chip
            icon={<PendingIcon sx={{ fontSize: 14 }} />}
            label="Processing"
            size="small"
            sx={{
              backgroundColor: "#fef3c7",
              color: "#92400e",
              fontWeight: 500,
              fontSize: "0.75rem",
              height: "24px",
            }}
          />
        );
      case "failed":
        return (
          <Chip
            icon={<FailedIcon sx={{ fontSize: 14 }} />}
            label="Failed"
            size="small"
            sx={{
              backgroundColor: "#fee2e2",
              color: "#991b1b",
              fontWeight: 500,
              fontSize: "0.75rem",
              height: "24px",
            }}
          />
        );
      default:
        return null;
    }
  };

  const handleViewClick = (transaction) => {
    setViewingTransaction(transaction);
    setShowViewModal(true);
  };

  const handleEditClick = (transaction) => {
    setSelectedTransaction(transaction);
    setShowEditModal(true);
  };

  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const years = Array.from(
    { length: 5 },
    (_, i) => new Date().getFullYear() - i,
  );

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background:
          theme.palette.mode === "dark"
            ? "linear-gradient(135deg, #0a0f1e 0%, #0f172a 50%, #1e1b2e 100%)"
            : "linear-gradient(135deg, #fffbeb 0%, #ffffff 50%, #fefce8 100%)",
        p: 3,
      }}
    >
      {/* Header Section */}
      <Box sx={{ mb: 4 }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems={{ sm: "center" }}
          justifyContent="space-between"
          sx={{ width: "100%" }}
        >
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            sx={{ flex: 1 }}
          >
            <Box
              component="img"
              src={processPayrollSvg}
              alt="Process Payroll"
              sx={{
                width: { xs: 64, lg: 80 },
                height: { xs: 64, lg: 80 },
                objectFit: "contain",
              }}
            />
            <Box>
              <Typography
                variant="h4"
                component="h1"
                sx={{
                  fontWeight: "bold",
                  color: theme.palette.mode === "dark" ? "#ffffff" : "#111827",
                }}
              >
                Process Payroll
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.mode === "dark" ? "#9ca3af" : "#6b7280",
                  mt: 0.5,
                }}
              >
                Manage monthly payroll processing and employee salary
                disbursement
              </Typography>
            </Box>
          </Stack>

          <Box>
            <Stack direction="row" spacing={2}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setShowCreateModal(true)}
                sx={{
                  background:
                    "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                  borderRadius: "10px",
                  px: 2.5,
                  py: 1,
                  fontSize: "0.875rem",
                  fontWeight: 500,
                  textTransform: "none",
                  minWidth: "auto",
                  whiteSpace: "nowrap",
                  boxShadow: "none",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    background:
                      "linear-gradient(135deg, #d97706 0%, #b45309 100%)",
                    boxShadow: "0 2px 8px rgba(245, 158, 11, 0.3)",
                    transform: "translateY(-1px)",
                  },
                  "&:active": {
                    transform: "translateY(0)",
                  },
                }}
              >
                Process Payroll
              </Button>

              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={() => setShowDownloadModal(true)}
                sx={{
                  borderRadius: "10px",
                  px: 2.5,
                  py: 1,
                  fontSize: "0.875rem",
                  fontWeight: 500,
                  textTransform: "none",
                  minWidth: "auto",
                  whiteSpace: "nowrap",
                  borderColor: "#f59e0b",
                  color: "#f59e0b",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    borderColor: "#d97706",
                    backgroundColor: "rgba(245,158,11,0.1)",
                    transform: "translateY(-1px)",
                  },
                  "&:active": {
                    transform: "translateY(0)",
                  },
                }}
              >
                Download Payslip
              </Button>
            </Stack>
          </Box>
        </Stack>
      </Box>

      {/* Filters Section */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        alignItems={{ sm: "center" }}
        justifyContent="space-between"
        sx={{ mb: 3 }}
      >
        <Stack direction="row" spacing={2}>
          <TextField
            select
            label="Month"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            size="small"
            sx={{
              minWidth: 140,
              "& .MuiOutlinedInput-root": {
                borderRadius: "12px",
                backgroundColor:
                  theme.palette.mode === "dark" ? "#1e293b" : "#ffffff",
              },
            }}
          >
            {months.map((m, idx) => (
              <MenuItem key={idx} value={idx + 1}>
                {m}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Year"
            value={year}
            onChange={(e) => setYear(e.target.value)}
            size="small"
            sx={{
              minWidth: 100,
              "& .MuiOutlinedInput-root": {
                borderRadius: "12px",
                backgroundColor:
                  theme.palette.mode === "dark" ? "#1e293b" : "#ffffff",
              },
            }}
          >
            {years.map((y) => (
              <MenuItem key={y} value={y}>
                {y}
              </MenuItem>
            ))}
          </TextField>
        </Stack>

        <TextField
          placeholder="Search by employee, code or department..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          variant="outlined"
          size="small"
          sx={{
            flex: 1,
            maxWidth: { sm: 400 },
            "& .MuiOutlinedInput-root": {
              borderRadius: "12px",
              backgroundColor:
                theme.palette.mode === "dark" ? "#1e293b" : "#ffffff",
              "& fieldset": {
                borderColor:
                  theme.palette.mode === "dark" ? "#334155" : "#e5e7eb",
              },
              "&:hover fieldset": {
                borderColor: "#f59e0b",
              },
              "&.Mui-focused fieldset": {
                borderColor: "#f59e0b",
                borderWidth: "2px",
              },
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: "#9ca3af" }} />
              </InputAdornment>
            ),
          }}
        />

        <Stack direction="row" spacing={1}>
          {["all", "processed", "approved", "paid", "failed"].map((filter) => (
            <Button
              key={filter}
              variant={statusFilter === filter ? "contained" : "outlined"}
              onClick={() => setStatusFilter(filter)}
              sx={{
                textTransform: "capitalize",
                borderRadius: "8px",
                px: 2,
                py: 0.7,
                fontSize: "0.875rem",
                fontWeight: 500,
                ...(statusFilter === filter && {
                  background:
                    "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                  boxShadow: "0 4px 6px -1px rgba(245, 158, 11, 0.25)",
                  "&:hover": {
                    background:
                      "linear-gradient(135deg, #d97706 0%, #b45309 100%)",
                  },
                }),
                ...(statusFilter !== filter && {
                  borderColor:
                    theme.palette.mode === "dark" ? "#475569" : "#d1d5db",
                  color: theme.palette.mode === "dark" ? "#d1d5db" : "#6b7280",
                  "&:hover": {
                    borderColor: "#f59e0b",
                    backgroundColor:
                      theme.palette.mode === "dark"
                        ? "rgba(245,158,11,0.1)"
                        : "#fffbeb",
                  },
                }),
              }}
            >
              {filter.charAt(0).toUpperCase() + filter.slice(1)}
            </Button>
          ))}
        </Stack>
      </Stack>

      {/* Summary Cards */}
      {summary && processInfo && (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Paper
              sx={{
                p: 2,
                borderRadius: "16px",
                background:
                  theme.palette.mode === "dark"
                    ? "linear-gradient(135deg, #1e293b 0%, #0f172a 100%)"
                    : "linear-gradient(135deg, #ffffff 0%, #fefce8 100%)",
                border: `1px solid ${
                  theme.palette.mode === "dark" ? "#334155" : "#fef3c7"
                }`,
              }}
            >
              <Stack direction="row" alignItems="center" spacing={2}>
                <Box
                  sx={{
                    p: 1,
                    borderRadius: "12px",
                    backgroundColor: "rgba(245, 158, 11, 0.1)",
                  }}
                >
                  <PayrollIcon sx={{ color: "#f59e0b" }} />
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: "#9ca3af" }}>
                    Process Status
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    {getProcessStatusChip(processInfo.status)}
                  </Typography>
                </Box>
              </Stack>
            </Paper>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Paper
              sx={{
                p: 2,
                borderRadius: "16px",
                background:
                  theme.palette.mode === "dark"
                    ? "linear-gradient(135deg, #1e293b 0%, #0f172a 100%)"
                    : "linear-gradient(135deg, #ffffff 0%, #fefce8 100%)",
                border: `1px solid ${
                  theme.palette.mode === "dark" ? "#334155" : "#fef3c7"
                }`,
              }}
            >
              <Stack direction="row" alignItems="center" spacing={2}>
                <Box
                  sx={{
                    p: 1,
                    borderRadius: "12px",
                    backgroundColor: "rgba(16, 185, 129, 0.1)",
                  }}
                >
                  <MoneyIcon sx={{ color: "#10b981" }} />
                </Box>
                <Stack direction="column" spacing={0.5}>
                  <Typography variant="caption" sx={{ color: "#9ca3af" }}>
                    Total Net Salary
                  </Typography>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Box
                      sx={{
                        p: 0.5,
                        borderRadius: "8px",
                        backgroundColor: "rgba(16, 185, 129, 0.1)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <MoneyIcon sx={{ color: "#10b981", fontSize: 16 }} />
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      {formatCurrency(summary.total_net_salary)}
                    </Typography>
                  </Stack>
                </Stack>
              </Stack>
            </Paper>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Paper
              sx={{
                p: 2,
                borderRadius: "16px",
                background:
                  theme.palette.mode === "dark"
                    ? "linear-gradient(135deg, #1e293b 0%, #0f172a 100%)"
                    : "linear-gradient(135deg, #ffffff 0%, #fefce8 100%)",
                border: `1px solid ${
                  theme.palette.mode === "dark" ? "#334155" : "#fef3c7"
                }`,
              }}
            >
              <Stack direction="row" alignItems="center" spacing={2}>
                <Box
                  sx={{
                    p: 1,
                    borderRadius: "12px",
                    backgroundColor: "rgba(239, 68, 68, 0.1)",
                  }}
                >
                  <CancelIcon sx={{ color: "#ef4444" }} />
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: "#9ca3af" }}>
                    Total Deductions
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    {formatCurrency(summary.total_deductions)}
                  </Typography>
                </Box>
              </Stack>
            </Paper>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Paper
              sx={{
                p: 2,
                borderRadius: "16px",
                background:
                  theme.palette.mode === "dark"
                    ? "linear-gradient(135deg, #1e293b 0%, #0f172a 100%)"
                    : "linear-gradient(135deg, #ffffff 0%, #fefce8 100%)",
                border: `1px solid ${
                  theme.palette.mode === "dark" ? "#334155" : "#fef3c7"
                }`,
              }}
            >
              <Stack direction="row" alignItems="center" spacing={2}>
                <Box
                  sx={{
                    p: 1,
                    borderRadius: "12px",
                    backgroundColor: "rgba(59, 130, 246, 0.1)",
                  }}
                >
                  <EventIcon sx={{ color: "#3b82f6" }} />
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: "#9ca3af" }}>
                    Total Employees
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    {summary.total_employees}
                  </Typography>
                </Box>
              </Stack>
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* Loading State */}
      {loading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: 400,
          }}
        >
          <Stack alignItems="center" spacing={2}>
            <CircularProgress size={48} sx={{ color: "#f59e0b" }} />
            <Typography
              sx={{
                color: theme.palette.mode === "dark" ? "#9ca3af" : "#6b7280",
              }}
            >
              Loading payroll transactions...
            </Typography>
          </Stack>
        </Box>
      ) : (
        <Paper
          elevation={0}
          sx={{
            borderRadius: "16px",
            overflow: "auto",
            boxShadow:
              theme.palette.mode === "dark"
                ? "0 10px 25px -5px rgba(0,0,0,0.3)"
                : "0 10px 25px -5px rgba(0,0,0,0.05), 0 8px 10px -6px rgba(0,0,0,0.02)",
            border: `1px solid ${
              theme.palette.mode === "dark" ? "#1e293b" : "#f3f4f6"
            }`,
          }}
        >
          <TableContainer sx={{ overflowX: "auto" }}>
            <Table sx={{ minWidth: 1000 }}>
              <TableHead
                sx={{
                  backgroundColor:
                    theme.palette.mode === "dark" ? "#1e293b" : "#f9fafb",
                }}
              >
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, fontSize: "0.75rem" }}>
                    ID & Tenant ID
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: "0.75rem" }}>
                    Employee
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: "0.75rem" }}>
                    Department
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{ fontWeight: 600, fontSize: "0.75rem" }}
                  >
                    Basic Salary
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{ fontWeight: 600, fontSize: "0.75rem" }}
                  >
                    Earnings
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{ fontWeight: 600, fontSize: "0.75rem" }}
                  >
                    Deductions
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{ fontWeight: 600, fontSize: "0.75rem" }}
                  >
                    Net Salary
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: "0.75rem" }}>
                    Status
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: "0.75rem" }}>
                    Payment Date
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{ fontWeight: 600, fontSize: "0.75rem" }}
                  >
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredTransactions.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 6 }}>
                      <ReceiptIcon
                        sx={{ fontSize: 48, color: "#d1d5db", mb: 1 }}
                      />
                      <Typography sx={{ color: "#6b7280" }}>
                        No payroll transactions found for {months[month - 1]}{" "}
                        {year}.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredTransactions.map((transaction, index) => (
                    <TableRow
                      key={transaction.id}
                      component={motion.tr}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.05 }}
                      sx={{
                        "&:hover": {
                          backgroundColor:
                            theme.palette.mode === "dark"
                              ? "rgba(30,41,59,0.5)"
                              : "#f9fafb",
                        },
                      }}
                    >
                      <TableCell>
                        <Stack spacing={0.5}>
                          <Typography
                            sx={{
                              fontSize: "0.75rem",
                              fontWeight: 600,
                              color: "#f59e0b",
                            }}
                          >
                            ID: {transaction.id}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "0.7rem",
                              color:
                                theme.palette.mode === "dark"
                                  ? "#9ca3af"
                                  : "#6b7280",
                            }}
                          >
                            Tenant: {transaction.tenant_id}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Stack spacing={0.5}>
                          <Typography
                            sx={{
                              fontWeight: 600,
                              fontSize: "0.875rem",
                              color:
                                theme.palette.mode === "dark"
                                  ? "#ffffff"
                                  : "#111827",
                            }}
                          >
                            {transaction.employee_name}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "0.7rem",
                              color:
                                theme.palette.mode === "dark"
                                  ? "#9ca3af"
                                  : "#6b7280",
                            }}
                          >
                            Code: {transaction.employee_code}
                          </Typography>
                        </Stack>
                      </TableCell>

                      <TableCell>
                        <Typography
                          sx={{
                            fontSize: "0.875rem",
                            color:
                              theme.palette.mode === "dark"
                                ? "#ffffff"
                                : "#111827",
                          }}
                        >
                          {transaction.department_name}
                        </Typography>
                      </TableCell>

                      <TableCell align="right">
                        <Typography
                          sx={{
                            fontWeight: 500,
                            fontSize: "0.875rem",
                          }}
                        >
                          {formatCurrency(transaction.basic_salary)}
                        </Typography>
                      </TableCell>

                      <TableCell align="right">
                        <Typography
                          sx={{
                            fontWeight: 500,
                            fontSize: "0.875rem",
                            color: "#10b981",
                          }}
                        >
                          {formatCurrency(transaction.total_earnings)}
                        </Typography>
                      </TableCell>

                      <TableCell align="right">
                        <Typography
                          sx={{
                            fontWeight: 500,
                            fontSize: "0.875rem",
                            color: "#ef4444",
                          }}
                        >
                          {formatCurrency(transaction.total_deductions)}
                        </Typography>
                      </TableCell>

                      <TableCell align="right">
                        <Typography
                          sx={{
                            fontWeight: 700,
                            fontSize: "0.875rem",
                            color: "#f59e0b",
                          }}
                        >
                          {formatCurrency(transaction.net_salary)}
                        </Typography>
                      </TableCell>

                      <TableCell>{getStatusChip(transaction.status)}</TableCell>

                      <TableCell>
                        <Typography sx={{ fontSize: "0.75rem" }}>
                          {transaction.payment_date
                            ? formatDate(transaction.payment_date)
                            : "-"}
                        </Typography>
                      </TableCell>

                      <TableCell align="center">
                        <Stack
                          direction="row"
                          spacing={0.5}
                          justifyContent="center"
                        >
                          <Tooltip title="View Details">
                            <IconButton
                              onClick={() => handleViewClick(transaction)}
                              size="small"
                              sx={{
                                color: "#3b82f6",
                                "&:hover": {
                                  backgroundColor:
                                    theme.palette.mode === "dark"
                                      ? "rgba(59,130,246,0.2)"
                                      : "#eff6ff",
                                },
                              }}
                            >
                              <ViewIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Update Status">
                            <IconButton
                              onClick={() => handleEditClick(transaction)}
                              size="small"
                              sx={{
                                color: "#f59e0b",
                                "&:hover": {
                                  backgroundColor:
                                    theme.palette.mode === "dark"
                                      ? "rgba(245,158,11,0.2)"
                                      : "#fffbeb",
                                },
                              }}
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Count Summary */}
      {!loading && transactions.length > 0 && (
        <Stack
          direction="row"
          justifyContent="space-between"
          sx={{
            mt: 2,
            gap: 2,
            fontSize: "0.875rem",
            color: theme.palette.mode === "dark" ? "#9ca3af" : "#6b7280",
          }}
        >
          <Typography variant="body2">
            Total Transactions: {transactions.length}
          </Typography>
          <Typography variant="body2">
            Showing: {filteredTransactions.length} of {transactions.length}
          </Typography>
        </Stack>
      )}

      {/* View Details Modal */}
      <Dialog
        open={showViewModal}
        onClose={() => setShowViewModal(false)}
        maxWidth="md"
        fullWidth
        TransitionComponent={motion.div}
        TransitionProps={{
          timeout: 300,
        }}
        PaperProps={{
          sx: {
            borderRadius: "28px",
            overflow: "hidden",
            maxWidth: "700px",
            margin: "16px",
            background:
              theme.palette.mode === "dark"
                ? "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)"
                : "linear-gradient(135deg, #ffffff 0%, #fefce8 100%)",
            boxShadow: theme.shadows[24],
            position: "relative",
          },
        }}
        slotProps={{
          backdrop: {
            sx: {
              backgroundColor: "rgba(0, 0, 0, 0.6)",
              backdropFilter: "blur(4px)",
            },
          },
        }}
      >
        {viewingTransaction && (
          <motion.div
            initial={{ y: 50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 50, opacity: 0 }}
            transition={{ type: "spring", damping: 25, stiffness: 200 }}
          >
            {/* Gradient Top Bar */}
            <Box
              sx={{
                background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                height: "5px",
              }}
            />

            {/* Header with SVG */}
            <Box
              sx={{
                p: 2.5,
                pr: 5,
                borderBottom: `1px solid ${
                  theme.palette.mode === "dark" ? "#1e293b" : "#f3f4f6"
                }`,
                backgroundColor:
                  theme.palette.mode === "dark"
                    ? "rgba(30,41,59,0.5)"
                    : "rgba(255,251,235,0.5)",
                position: "relative",
              }}
            >
              <Stack direction="row" spacing={2} alignItems="center">
                <Box
                  component="img"
                  src={viewDetailsSvg}
                  alt="Details"
                  sx={{
                    width: 70,
                    height: 70,
                    objectFit: "contain",
                    filter: "drop-shadow(0 2px 4px rgba(245,158,11,0.3))",
                  }}
                />
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 800,
                    background:
                      "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                    backgroundClip: "text",
                    WebkitBackgroundClip: "text",
                    color: "transparent",
                  }}
                >
                  Payroll Transaction Details
                </Typography>
              </Stack>

              <IconButton
                onClick={() => setShowViewModal(false)}
                sx={{
                  position: "absolute",
                  top: 18,
                  right: 18,
                  width: 32,
                  height: 32,
                  borderRadius: "10px",
                  color: theme.palette.mode === "dark" ? "#9ca3af" : "#6b7280",
                  "&:hover": {
                    background: "#f59e0b",
                    color: "#ffffff",
                  },
                }}
              >
                <CloseIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Box>

            {/* Single Scrollable Content */}
            <Box
              sx={{
                maxHeight: "calc(80vh - 200px)",
                overflowY: "auto",
                overflowX: "hidden",
                "&::-webkit-scrollbar": {
                  width: "5px",
                },
                "&::-webkit-scrollbar-track": {
                  background:
                    theme.palette.mode === "dark" ? "#1e293b" : "#e2e8f0",
                  borderRadius: "10px",
                },
                "&::-webkit-scrollbar-thumb": {
                  background: "#f59e0b",
                  borderRadius: "10px",
                },
              }}
            >
              <Box sx={{ p: 3 }}>
                <Stack spacing={2.5}>
                  {/* Row 1: Employee Information + Payment Information */}
                  <Grid
                    container
                    spacing={2}
                    display="flex"
                    alignItems="stretch"
                    sx={{ width: "100%", margin: 0 }}
                  >
                    <Grid item xs={12} md={6}>
                      <Paper
                        elevation={0}
                        sx={{
                          p: 2,
                          borderRadius: "20px",
                          height: "100%",
                          background:
                            theme.palette.mode === "dark"
                              ? "rgba(30,41,59,0.6)"
                              : "#fefce8",
                          border: `1px solid ${
                            theme.palette.mode === "dark"
                              ? "#334155"
                              : "#fef3c7"
                          }`,
                        }}
                      >
                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="center"
                          sx={{ mb: 2 }}
                        >
                          <Box
                            sx={{
                              width: 4,
                              height: 20,
                              borderRadius: "2px",
                              background: "#f59e0b",
                            }}
                          />
                          <Typography
                            sx={{ fontWeight: 700, fontSize: "0.85rem" }}
                          >
                            Employee Information
                          </Typography>
                        </Stack>
                        <Stack spacing={1.5}>
                          <Stack
                            direction="row"
                            justifyContent="space-between"
                            sx={{ gap: "16px" }}
                          >
                            <Typography
                              variant="caption"
                              sx={{ color: "#9ca3af", minWidth: "110px" }}
                            >
                              Employee Name
                            </Typography>
                            <Typography
                              sx={{
                                fontWeight: 600,
                                fontSize: "0.85rem",
                                textAlign: "right",
                              }}
                            >
                              {viewingTransaction.employee_name}
                            </Typography>
                          </Stack>
                          <Stack
                            direction="row"
                            justifyContent="space-between"
                            sx={{ gap: "16px" }}
                          >
                            <Typography
                              variant="caption"
                              sx={{ color: "#9ca3af", minWidth: "110px" }}
                            >
                              Employee Code
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "0.85rem",
                                textAlign: "right",
                              }}
                            >
                              {viewingTransaction.employee_code}
                            </Typography>
                          </Stack>
                          <Stack
                            direction="row"
                            justifyContent="space-between"
                            sx={{ gap: "16px" }}
                          >
                            <Typography
                              variant="caption"
                              sx={{ color: "#9ca3af", minWidth: "110px" }}
                            >
                              Employee ID
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "0.85rem",
                                textAlign: "right",
                              }}
                            >
                              {viewingTransaction.employee_id}
                            </Typography>
                          </Stack>
                          <Stack
                            direction="row"
                            justifyContent="space-between"
                            sx={{ gap: "16px" }}
                          >
                            <Typography
                              variant="caption"
                              sx={{ color: "#9ca3af", minWidth: "110px" }}
                            >
                              Department
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "0.85rem",
                                textAlign: "right",
                              }}
                            >
                              {viewingTransaction.department_name || "-"}
                            </Typography>
                          </Stack>
                        </Stack>
                      </Paper>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Paper
                        elevation={0}
                        sx={{
                          p: 2,
                          borderRadius: "20px",
                          height: "100%",
                          background:
                            theme.palette.mode === "dark"
                              ? "rgba(30,41,59,0.6)"
                              : "#fefce8",
                          border: `1px solid ${
                            theme.palette.mode === "dark"
                              ? "#334155"
                              : "#fef3c7"
                          }`,
                        }}
                      >
                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="center"
                          sx={{ mb: 2 }}
                        >
                          <Box
                            sx={{
                              width: 4,
                              height: 20,
                              borderRadius: "2px",
                              background: "#f59e0b",
                            }}
                          />
                          <Typography
                            sx={{ fontWeight: 700, fontSize: "0.85rem" }}
                          >
                            Payment Information
                          </Typography>
                        </Stack>
                        <Stack spacing={1.5}>
                          <Stack
                            direction="row"
                            justifyContent="space-between"
                            alignItems="center"
                            sx={{ gap: "16px" }}
                          >
                            <Typography
                              variant="caption"
                              sx={{ color: "#9ca3af", minWidth: "110px" }}
                            >
                              Status
                            </Typography>
                            {getStatusChip(viewingTransaction.status)}
                          </Stack>
                          <Stack
                            direction="row"
                            justifyContent="space-between"
                            sx={{ gap: "16px" }}
                          >
                            <Typography
                              variant="caption"
                              sx={{ color: "#9ca3af", minWidth: "110px" }}
                            >
                              Payment Date
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "0.85rem",
                                textAlign: "right",
                              }}
                            >
                              {viewingTransaction.payment_date
                                ? formatDate(viewingTransaction.payment_date)
                                : "-"}
                            </Typography>
                          </Stack>
                          <Stack
                            direction="row"
                            justifyContent="space-between"
                            sx={{ gap: "16px" }}
                          >
                            <Typography
                              variant="caption"
                              sx={{ color: "#9ca3af", minWidth: "110px" }}
                            >
                              Payment Reference
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "0.85rem",
                                fontFamily: "monospace",
                                textAlign: "right",
                              }}
                            >
                              {viewingTransaction.payment_reference || "-"}
                            </Typography>
                          </Stack>
                          <Stack
                            direction="row"
                            justifyContent="space-between"
                            sx={{ gap: "16px" }}
                          >
                            <Typography
                              variant="caption"
                              sx={{ color: "#9ca3af", minWidth: "110px" }}
                            >
                              Remarks
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "0.85rem",
                                textAlign: "right",
                              }}
                            >
                              {viewingTransaction.remarks || "-"}
                            </Typography>
                          </Stack>
                        </Stack>
                      </Paper>
                    </Grid>
                  </Grid>

                  {/* Row 2: Salary Breakdown */}
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      borderRadius: "20px",
                      background:
                        theme.palette.mode === "dark"
                          ? "rgba(30,41,59,0.6)"
                          : "#fefce8",
                      border: `1px solid ${
                        theme.palette.mode === "dark" ? "#334155" : "#fef3c7"
                      }`,
                    }}
                  >
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      sx={{ mb: 2 }}
                    >
                      <MoneyIcon sx={{ fontSize: 16, color: "#f59e0b" }} />
                      <Typography sx={{ fontWeight: 700, fontSize: "0.85rem" }}>
                        Salary Breakdown
                      </Typography>
                    </Stack>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Stack spacing={1}>
                          <Typography
                            variant="caption"
                            sx={{ color: "#9ca3af" }}
                          >
                            Basic Salary
                          </Typography>
                          <Typography fontWeight={500}>
                            {formatCurrency(viewingTransaction.basic_salary)}
                          </Typography>
                        </Stack>
                      </Grid>
                      <Grid item xs={6}>
                        <Stack spacing={1}>
                          <Typography
                            variant="caption"
                            sx={{ color: "#9ca3af" }}
                          >
                            HRA
                          </Typography>
                          <Typography fontWeight={500}>
                            {formatCurrency(viewingTransaction.hra)}
                          </Typography>
                        </Stack>
                      </Grid>
                      <Grid item xs={6}>
                        <Stack spacing={1}>
                          <Typography
                            variant="caption"
                            sx={{ color: "#9ca3af" }}
                          >
                            Conveyance
                          </Typography>
                          <Typography fontWeight={500}>
                            {formatCurrency(viewingTransaction.conveyance)}
                          </Typography>
                        </Stack>
                      </Grid>
                      <Grid item xs={6}>
                        <Stack spacing={1}>
                          <Typography
                            variant="caption"
                            sx={{ color: "#9ca3af" }}
                          >
                            Education Allowance
                          </Typography>
                          <Typography fontWeight={500}>
                            {formatCurrency(
                              viewingTransaction.education_allowance,
                            )}
                          </Typography>
                        </Stack>
                      </Grid>
                      <Grid item xs={6}>
                        <Stack spacing={1}>
                          <Typography
                            variant="caption"
                            sx={{ color: "#9ca3af" }}
                          >
                            Transport Allowance
                          </Typography>
                          <Typography fontWeight={500}>
                            {formatCurrency(
                              viewingTransaction.transport_allowance,
                            )}
                          </Typography>
                        </Stack>
                      </Grid>
                      <Grid item xs={6}>
                        <Stack spacing={1}>
                          <Typography
                            variant="caption"
                            sx={{ color: "#9ca3af" }}
                          >
                            Fixed Allowance
                          </Typography>
                          <Typography fontWeight={500}>
                            {formatCurrency(viewingTransaction.fixed_allowance)}
                          </Typography>
                        </Stack>
                      </Grid>
                      <Grid item xs={6}>
                        <Stack spacing={1}>
                          <Typography
                            variant="caption"
                            sx={{ color: "#9ca3af" }}
                          >
                            Bonus
                          </Typography>
                          <Typography fontWeight={500}>
                            {formatCurrency(viewingTransaction.bonus)}
                          </Typography>
                        </Stack>
                      </Grid>
                      <Grid item xs={6}>
                        <Stack spacing={1}>
                          <Typography
                            variant="caption"
                            sx={{ color: "#9ca3af" }}
                          >
                            Overtime Amount
                          </Typography>
                          <Typography fontWeight={500}>
                            {formatCurrency(viewingTransaction.overtime_amount)}
                          </Typography>
                        </Stack>
                      </Grid>
                      <Grid item xs={6}>
                        <Stack spacing={1}>
                          <Typography
                            variant="caption"
                            sx={{ color: "#9ca3af" }}
                          >
                            Gratuity
                          </Typography>
                          <Typography fontWeight={500}>
                            {formatCurrency(viewingTransaction.gratuity)}
                          </Typography>
                        </Stack>
                      </Grid>
                      <Grid item xs={6}>
                        <Stack spacing={1}>
                          <Typography
                            variant="caption"
                            sx={{ color: "#9ca3af" }}
                          >
                            Commission
                          </Typography>
                          <Typography fontWeight={500}>
                            {formatCurrency(viewingTransaction.commission)}
                          </Typography>
                        </Stack>
                      </Grid>
                    </Grid>
                  </Paper>

                  {/* Row 3: Reimbursements */}
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      borderRadius: "20px",
                      background:
                        theme.palette.mode === "dark"
                          ? "rgba(30,41,59,0.6)"
                          : "#fefce8",
                      border: `1px solid ${
                        theme.palette.mode === "dark" ? "#334155" : "#fef3c7"
                      }`,
                    }}
                  >
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      sx={{ mb: 2 }}
                    >
                      <Box
                        sx={{
                          width: 4,
                          height: 20,
                          borderRadius: "2px",
                          background: "#f59e0b",
                        }}
                      />
                      <Typography sx={{ fontWeight: 700, fontSize: "0.85rem" }}>
                        Reimbursements
                      </Typography>
                    </Stack>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Stack spacing={1}>
                          <Typography
                            variant="caption"
                            sx={{ color: "#9ca3af" }}
                          >
                            Telephone Reimbursement
                          </Typography>
                          <Typography fontWeight={500}>
                            {formatCurrency(
                              viewingTransaction.telephone_reimbursement,
                            )}
                          </Typography>
                        </Stack>
                      </Grid>
                      <Grid item xs={6}>
                        <Stack spacing={1}>
                          <Typography
                            variant="caption"
                            sx={{ color: "#9ca3af" }}
                          >
                            Fuel Reimbursement
                          </Typography>
                          <Typography fontWeight={500}>
                            {formatCurrency(
                              viewingTransaction.fuel_reimbursement,
                            )}
                          </Typography>
                        </Stack>
                      </Grid>
                      <Grid item xs={6}>
                        <Stack spacing={1}>
                          <Typography
                            variant="caption"
                            sx={{ color: "#9ca3af" }}
                          >
                            Driver Reimbursement
                          </Typography>
                          <Typography fontWeight={500}>
                            {formatCurrency(
                              viewingTransaction.driver_reimbursement,
                            )}
                          </Typography>
                        </Stack>
                      </Grid>
                      <Grid item xs={6}>
                        <Stack spacing={1}>
                          <Typography
                            variant="caption"
                            sx={{ color: "#9ca3af" }}
                          >
                            Vehicle Maintenance
                          </Typography>
                          <Typography fontWeight={500}>
                            {formatCurrency(
                              viewingTransaction.vehicle_maintenance_reimbursement,
                            )}
                          </Typography>
                        </Stack>
                      </Grid>
                      <Grid item xs={6}>
                        <Stack spacing={1}>
                          <Typography
                            variant="caption"
                            sx={{ color: "#9ca3af" }}
                          >
                            LTA Reimbursement
                          </Typography>
                          <Typography fontWeight={500}>
                            {formatCurrency(
                              viewingTransaction.lta_reimbursement,
                            )}
                          </Typography>
                        </Stack>
                      </Grid>
                      <Grid item xs={6}>
                        <Stack spacing={1}>
                          <Typography
                            variant="caption"
                            sx={{ color: "#9ca3af" }}
                          >
                            Total Reimbursements
                          </Typography>
                          <Typography fontWeight={600} color="#10b981">
                            {formatCurrency(
                              viewingTransaction.total_reimbursements,
                            )}
                          </Typography>
                        </Stack>
                      </Grid>
                    </Grid>
                  </Paper>

                  {/* Row 4: Deductions */}
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      borderRadius: "20px",
                      background:
                        theme.palette.mode === "dark"
                          ? "rgba(30,41,59,0.6)"
                          : "#fefce8",
                      border: `1px solid ${
                        theme.palette.mode === "dark" ? "#334155" : "#fef3c7"
                      }`,
                    }}
                  >
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      sx={{ mb: 2 }}
                    >
                      <PercentIcon sx={{ fontSize: 16, color: "#f59e0b" }} />
                      <Typography sx={{ fontWeight: 700, fontSize: "0.85rem" }}>
                        Deductions & Taxes
                      </Typography>
                    </Stack>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Stack spacing={1}>
                          <Typography
                            variant="caption"
                            sx={{ color: "#9ca3af" }}
                          >
                            Provident Fund
                          </Typography>
                          <Typography fontWeight={500}>
                            {formatCurrency(viewingTransaction.provident_fund)}
                          </Typography>
                        </Stack>
                      </Grid>
                      <Grid item xs={6}>
                        <Stack spacing={1}>
                          <Typography
                            variant="caption"
                            sx={{ color: "#9ca3af" }}
                          >
                            Professional Tax
                          </Typography>
                          <Typography fontWeight={500}>
                            {formatCurrency(
                              viewingTransaction.professional_tax,
                            )}
                          </Typography>
                        </Stack>
                      </Grid>
                      <Grid item xs={6}>
                        <Stack spacing={1}>
                          <Typography
                            variant="caption"
                            sx={{ color: "#9ca3af" }}
                          >
                            Tax Amount (TDS)
                          </Typography>
                          <Typography fontWeight={500}>
                            {formatCurrency(viewingTransaction.tax_amount)}
                          </Typography>
                        </Stack>
                      </Grid>
                      <Grid item xs={6}>
                        <Stack spacing={1}>
                          <Typography
                            variant="caption"
                            sx={{ color: "#9ca3af" }}
                          >
                            Late Deduction
                          </Typography>
                          <Typography fontWeight={500}>
                            {formatCurrency(viewingTransaction.late_deduction)}
                          </Typography>
                        </Stack>
                      </Grid>
                      <Grid item xs={6}>
                        <Stack spacing={1}>
                          <Typography
                            variant="caption"
                            sx={{ color: "#9ca3af" }}
                          >
                            Loan Installment
                          </Typography>
                          <Typography fontWeight={500}>
                            {formatCurrency(
                              viewingTransaction.loan_installment,
                            )}
                          </Typography>
                        </Stack>
                      </Grid>
                    </Grid>
                  </Paper>

                  {/* Summary Cards */}
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Paper
                        elevation={0}
                        sx={{
                          p: 2,
                          borderRadius: "20px",
                          background:
                            theme.palette.mode === "dark"
                              ? "rgba(30,41,59,0.6)"
                              : "#fefce8",
                          border: `1px solid ${
                            theme.palette.mode === "dark"
                              ? "#334155"
                              : "#fef3c7"
                          }`,
                        }}
                      >
                        <Typography variant="caption" sx={{ color: "#9ca3af" }}>
                          Total Earnings
                        </Typography>
                        <Typography
                          sx={{
                            fontWeight: 800,
                            fontSize: "1.25rem",
                            color: "#10b981",
                          }}
                        >
                          {formatCurrency(viewingTransaction.total_earnings)}
                        </Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={6}>
                      <Paper
                        elevation={0}
                        sx={{
                          p: 2,
                          borderRadius: "20px",
                          background:
                            theme.palette.mode === "dark"
                              ? "rgba(30,41,59,0.6)"
                              : "#fefce8",
                          border: `1px solid ${
                            theme.palette.mode === "dark"
                              ? "#334155"
                              : "#fef3c7"
                          }`,
                        }}
                      >
                        <Typography variant="caption" sx={{ color: "#9ca3af" }}>
                          Total Deductions
                        </Typography>
                        <Typography
                          sx={{
                            fontWeight: 800,
                            fontSize: "1.25rem",
                            color: "#ef4444",
                          }}
                        >
                          {formatCurrency(viewingTransaction.total_deductions)}
                        </Typography>
                      </Paper>
                    </Grid>
                  </Grid>

                  {/* Net Salary Highlight */}
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2.5,
                      borderRadius: "20px",
                      background:
                        theme.palette.mode === "dark"
                          ? "linear-gradient(135deg, #1e293b 0%, #0f172a 100%)"
                          : "linear-gradient(135deg, #fefce8 0%, #ffffff 100%)",
                      border: `2px solid #f59e0b`,
                    }}
                  >
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      sx={{ gap: "16px" }}
                    >
                      <Typography
                        sx={{
                          fontWeight: 700,
                          fontSize: "1rem",
                          color: "#f59e0b",
                        }}
                      >
                        Net Salary
                      </Typography>
                      <Typography
                        sx={{
                          fontWeight: 800,
                          fontSize: "1.5rem",
                          color: "#f59e0b",
                          textAlign: "right",
                        }}
                      >
                        {formatCurrency(viewingTransaction.net_salary)}
                      </Typography>
                    </Stack>
                  </Paper>

                  {/* Timestamps */}
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      borderRadius: "20px",
                      background:
                        theme.palette.mode === "dark"
                          ? "rgba(30,41,59,0.6)"
                          : "#fefce8",
                      border: `1px solid ${
                        theme.palette.mode === "dark" ? "#334155" : "#fef3c7"
                      }`,
                    }}
                  >
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      sx={{ mb: 2 }}
                    >
                      <EventIcon sx={{ fontSize: 16, color: "#f59e0b" }} />
                      <Typography sx={{ fontWeight: 700, fontSize: "0.85rem" }}>
                        Timestamps
                      </Typography>
                    </Stack>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          sx={{ gap: "16px", mb: 1 }}
                        >
                          <Typography
                            variant="caption"
                            sx={{ color: "#9ca3af", minWidth: "90px" }}
                          >
                            Completed At
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "0.75rem",
                              textAlign: "right",
                            }}
                          >
                            {formatDateTime(viewingTransaction.processed_at)}
                          </Typography>
                        </Stack>
                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          sx={{ gap: "16px" }}
                        >
                          <Typography
                            variant="caption"
                            sx={{ color: "#9ca3af", minWidth: "90px" }}
                          >
                            Month/Year
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "0.75rem",
                              textAlign: "right",
                            }}
                          >
                            {months[viewingTransaction.month - 1]}{" "}
                            {viewingTransaction.year}
                          </Typography>
                        </Stack>
                      </Grid>
                      <Grid item xs={6}>
                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          sx={{ gap: "16px", mb: 1 }}
                        >
                          <Typography
                            variant="caption"
                            sx={{ color: "#9ca3af", minWidth: "90px" }}
                          >
                            Created At
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "0.75rem",
                              textAlign: "right",
                            }}
                          >
                            {formatDateTime(viewingTransaction.created_at)}
                          </Typography>
                        </Stack>
                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          sx={{ gap: "16px" }}
                        >
                          <Typography
                            variant="caption"
                            sx={{ color: "#9ca3af", minWidth: "90px" }}
                          >
                            Updated At
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "0.75rem",
                              opacity: 0.7,
                              textAlign: "right",
                            }}
                          >
                            {formatDateTime(viewingTransaction.updated_at)}
                          </Typography>
                        </Stack>
                      </Grid>
                    </Grid>
                  </Paper>
                </Stack>
              </Box>
            </Box>

            {/* Footer */}
            <Box
              sx={{
                p: 2,
                borderTop: `1px solid ${
                  theme.palette.mode === "dark" ? "#1e293b" : "#f3f4f6"
                }`,
                display: "flex",
                justifyContent: "flex-end",
              }}
            >
              <Button
                onClick={() => setShowViewModal(false)}
                variant="contained"
                sx={{
                  borderRadius: "14px",
                  px: 3,
                  py: 0.7,
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "0.8rem",
                  background:
                    "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                  "&:hover": {
                    background:
                      "linear-gradient(135deg, #d97706 0%, #b45309 100%)",
                  },
                }}
              >
                Close
              </Button>
            </Box>
          </motion.div>
        )}
      </Dialog>

      {/* Modals */}
      <AddProcessPayrollModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={() => dispatch(fetchMonthlyPayrollReport({ month, year }))}
        month={month}
        year={year}
      />

      <EditTransactionStatusModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setSelectedTransaction(null);
        }}
        transaction={selectedTransaction}
        onSuccess={() => dispatch(fetchMonthlyPayrollReport({ month, year }))}
      />

      {/* Download Payslip Modal */}
      <DownloadPayslipModal
        isOpen={showDownloadModal}
        onClose={() => setShowDownloadModal(false)}
        transactions={filteredTransactions}
        month={month}
        year={year}
      />
    </Box>
  );
};

export default ProcessPayroll;
